import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import {
  getDocumentIcon,
  jsonDeepClone,
  notify,
  OutsideClickDirective,
  SiaColorContrastAutoDirective,
  SiaHttpService,
  SiaInitDirective,
  SiaNewRouterService,
  SiaProfileNotification,
  SiaRouteData,
  SiaRouteMode,
  SiaSessionService,
  SiaSignalrService,
  SiaSizeDetectorService,
  SiaTooltipDirective,
  SiaTranslatePipe,
  VoiceToTextService,
} from '@sia/shared';
import { Guid } from 'guid-typescript';
import $ from 'jquery';
import { Subscription } from 'rxjs';
import { AIDocumentationCategories } from '../../interfaces/ai-documentation-categories';
import { DocumentationOrigin } from '../../interfaces/documentation-origin';
import { SiaMcpCollection } from '../../interfaces/mcp-tools';
import { UploadedChatFiles } from '../../interfaces/uploaded-chat-files';
import { MarkdownWithColorPipe } from '../../pipes/markdown-with-color.pipe';
import { SiaAIChatService } from '../../services/ai-chat.service';
import { AiFilePreviewComponent } from '../ai-file-preview/ai-file-preview.component';
import { AiPreviewHandlerComponent } from '../ai-preview-handler/ai-preview-handler.component';

@Component({
  selector: 'sia-ai-chat',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatChipsModule,
    MatCheckboxModule,
    MatTreeModule,
    DragDropModule,
    TextFieldModule,
    SiaTranslatePipe,
    MarkdownWithColorPipe,
    SiaInitDirective,
    MatDivider,
    MatTooltip,
    AiFilePreviewComponent,
    SiaTooltipDirective,
    SiaColorContrastAutoDirective,
    OutsideClickDirective,
    AiPreviewHandlerComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss',
})
export class SiaAiChatComponent implements AfterViewInit, OnDestroy {
  selectedDocumentationCategories = signal<{ [key: string]: string[] }>({});
  #siaNewRouterService = inject(SiaNewRouterService);
  #siaSignalrService = inject(SiaSignalrService);
  #siaAIChatService = inject(SiaAIChatService);
  #siaHttpService = inject(SiaHttpService);
  #elementRef = inject(ElementRef);
  #siaSessionService = inject(SiaSessionService);
  #subs: Subscription[] = [];
  voiceToTextService = inject(VoiceToTextService);
  #siaSizeDetectorService = inject(SiaSizeDetectorService);

  #llmChatHistory = computed(() => {
    let history = this.chatHistory()[this.activeChatSessionId()] || [];
    let llmMessages: {
      role: 'user' | 'assistant';
      content: string;
    }[] = [];
    history.forEach((h) => {
      llmMessages.push({ role: 'user', content: h.user });
      llmMessages.push({
        role: 'assistant',
        content: h.ai,
      });
    });
    return llmMessages;
  });

  @ViewChild('chatInputTextArea') chatInputElement?: ElementRef;
  @ViewChild('docReferenceMenuTrigger') docReferenceMenuTrigger?: any;

  collections = signal<SiaMcpCollection[]>([]);
  showPrompt = signal<boolean>(false);
  chatInput = '';

  // Document reference menu properties
  docMenuDocuments = signal<DocumentationOrigin[]>([]);
  docMenuPosition = signal<{ x: string; y: string }>({ x: '0px', y: '0px' });
  chatHistory = signal<{
    [key: string]: {
      user: string;
      ai: string;
      aiSanitized: string;
      attachedDocuments?: UploadedChatFiles[];
      chatUniqueId: string;
      isLoading?: boolean;
    }[];
  }>({});
  //sessionId => chatId => DocumentationOrigin[]
  chatHistoryOrigins = signal<{
    [key: string]: {
      [key: string]: DocumentationOrigin[];
    };
  }>({});

  // Chat sessions management
  chatSessions = signal<
    Array<{
      id: string;
      title: string;
      collectionId: string;
      outlet: string;
      lastActivity: Date;
      messages: {
        user: string;
        ai: string;
        aiSanitized: string;
        attachedDocuments?: UploadedChatFiles[];
        chatUniqueId: string;
        isLoading?: boolean;
      }[];
    }>
  >([]);
  activeChatSessionId = signal<string>('');

  // Computed property to get sessions for the current collection/outlet
  filteredChatSessions = computed(() => {
    const currentCollectionId = this.selectedCollectionId();
    const currentOutlet = this.selectedOutlet();
    return this.chatSessions()
      .filter(
        (session) =>
          session.collectionId === currentCollectionId &&
          session.outlet === currentOutlet,
      )
      .sort(
        (a, b) =>
          new Date(b.lastActivity).getTime() -
          new Date(a.lastActivity).getTime(),
      );
  });

  promptPosition = { x: 0, y: 0 };
  isDragging = signal<boolean>(false);
  routeDataForOutlet = signal<{ [key: string]: SiaRouteData | undefined }>({});
  routeDataOutlets = computed<string[]>(() => {
    return Object.keys(this.routeDataForOutlet());
  });
  selectedOutlet = signal<string>('');
  #voiceChangeSub?: Subscription;
  chatLoading = signal<{ [key: string]: boolean }>({});
  isNarrow = signal<boolean>(false);
  showHeaderActions = computed<boolean>(() => {
    return (
      (!this.lockChatFullScreen() && !this.isFullscreen()) || this.isNarrow()
    );
  });
  #resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  selectedCollectionId = signal<string>('');
  selectedCollection = computed<SiaMcpCollection | undefined>(() => {
    return this.collections().find((c) => c.id == this.selectedCollectionId());
  });
  isUserFullscreen = signal<boolean>(false);
  isFullscreen = computed<boolean>(() => {
    return this.isUserFullscreen() || this.lockChatFullScreen();
  });
  forceEmbedded = input<boolean>(false);
  forceNarrow = input<boolean>(false);
  hideFullscreenButton = input<boolean>(false);
  lockChatFullScreen = signal<boolean>(false);
  hasUserSentMessage = computed<boolean>(() => {
    const selectedId = this.activeChatSessionId();
    if (!selectedId) return false;
    const history = this.chatHistory()[selectedId];
    return !!(history && history.length > 0);
  });

  // Check if collection selection should be locked (after user has sent a message OR when using reference documents)
  isCollectionLocked = computed<boolean>(() => {
    // Lock if user has sent messages and collection is selected
    const hasMessagesAndCollection =
      this.hasUserSentMessage() && !!this.selectedCollectionId();

    // Also lock if session has reference documents (even before sending messages)
    const hasReferenceDocuments = this.hasCurrentSessionReferenceDocuments();

    return hasMessagesAndCollection || hasReferenceDocuments;
  });

  // Upload dei file tramite chat
  fileTypeAccepted: string[] = ['*/*'];
  currentUploadedFiles = signal<UploadedChatFiles[]>([]);
  maxFileNumber: number = 3;

  // Document selection for chat
  selectedDocuments = signal<Set<string>>(new Set());
  hasSelectedDocuments = computed(() => this.selectedDocuments().size > 0);

  // Reference documents per session (documents that are used as context for the entire chat)
  sessionReferenceDocuments = signal<{
    [sessionId: string]: DocumentationOrigin[];
  }>({});

  // Request tracking for cancellation - now per session
  #currentRequestId = signal<string | null>(null);
  #sessionRequestIds = signal<{ [sessionId: string]: string | null }>({});

  filteredCollections = computed<{ [key: string]: SiaMcpCollection[] }>(() => {
    let filteredCollections: { [key: string]: SiaMcpCollection[] } = {};
    const routeDataForOutlet = this.routeDataForOutlet();
    let genericCollections = this.collections().filter(
      (x) => !x.componentTypeId && !x.isDocumentationCollection,
    );
    let documentationCollections = this.collections().filter(
      (x) => x.isDocumentationCollection,
    );
    let popupVisible: boolean = false;
    if (routeDataForOutlet['popup']) {
      popupVisible = true;
    }
    for (const key in routeDataForOutlet) {
      if (!popupVisible || key.startsWith('popup')) {
        let routeData = routeDataForOutlet[key];
        if (!routeData?.menu?.menuType) {
          filteredCollections[key] = [];
        }
        filteredCollections[key] = this.collections().filter(
          (x) =>
            x.componentTypeId == routeData?.menu?.menuType &&
            x.mode == routeData?.mode,
        );
      }
    }
    for (const key in filteredCollections) {
      if (filteredCollections[key].length == 0) {
        delete filteredCollections[key];
      }
    }
    filteredCollections['generic'] = genericCollections;
    filteredCollections['documentation'] = documentationCollections;
    return filteredCollections;
  });
  #destroyRef = inject(DestroyRef);
  #scrollTimeout?: any;
  isMobile = signal<boolean>(
    this.#siaSizeDetectorService.currentSize?.isMobile ?? false,
  );
  showMobileHistory = signal<boolean>(false);
  sidebarClosed = signal<boolean>(true);
  documentationCategories = signal<AIDocumentationCategories[]>([]);

  selectedCategoryIds = signal<Set<string>>(new Set());

  // Tree control for nested categories
  treeControl = new NestedTreeControl<AIDocumentationCategories>(
    (node) => node.children,
  );
  treeDataSource = new MatTreeNestedDataSource<AIDocumentationCategories>();

  // Tree categories structure based on selected collection
  treeCategories = computed(() => {
    const collection = this.selectedCollection();
    if (!collection) return [];

    // For now, return tree categories for specific collection types
    // This can be expanded to load from API based on collection
    if (
      collection.id === 'documentation' ||
      collection.isDocumentationCollection
    ) {
      return this.documentationCategories();
    }

    // For other collections, return different tree structures or empty array
    return [];
  });

  // Computed signal to get all unique documents from the current session
  currentSessionDocuments = computed(() => {
    const currentSessionId = this.activeChatSessionId();
    if (!currentSessionId) return [];

    const sessionOrigins = this.chatHistoryOrigins()[currentSessionId];
    if (!sessionOrigins) return [];

    // Get current chat history to verify which messages still exist
    const currentHistory = this.chatHistory()[currentSessionId] || [];
    const existingMessageIds = new Set(
      currentHistory
        .filter((x) => !!x.aiSanitized)
        .map((msg) => msg.chatUniqueId),
    );

    // Collect all documents from messages that still exist in the chat history
    const allDocuments: DocumentationOrigin[] = [];
    Object.entries(sessionOrigins).forEach(([messageId, messageDocuments]) => {
      // Only include documents from messages that still exist in the chat history
      if (
        existingMessageIds.has(messageId) &&
        Array.isArray(messageDocuments)
      ) {
        allDocuments.push(...messageDocuments);
      }
    });

    // Remove duplicates based on fileSha256
    const uniqueDocuments = allDocuments.filter(
      (doc, index, self) =>
        index === self.findIndex((d) => d.fileSha256 === doc.fileSha256),
    );

    return uniqueDocuments.filter((doc) => doc.usedDocument);
  });

  // Check if tree categories are available for current collection
  hasTreeCategories = computed(() => {
    return this.selectedCollectionId() && this.treeCategories().length > 0;
  });

  // Check if current session has reference documents
  hasReferenceDocuments = computed(() => {
    const currentSessionId = this.activeChatSessionId();
    if (!currentSessionId) return false;

    const sessionOrigins = this.chatHistoryOrigins()[currentSessionId];
    if (!sessionOrigins) return false;

    // Check if any message in the session has documents
    return Object.values(sessionOrigins).some(
      (origins) => Array.isArray(origins) && origins.length > 0,
    );
  });

  // Get reference documents for the current session
  currentSessionReferenceDocuments = computed(() => {
    const currentSessionId = this.activeChatSessionId();
    if (!currentSessionId) return [];

    return this.sessionReferenceDocuments()[currentSessionId] || [];
  });

  // Check if current session has reference documents
  hasCurrentSessionReferenceDocuments = computed(() => {
    return this.currentSessionReferenceDocuments().length > 0;
  });

  // Loading text with typewriter animation
  siaAILoadingText = signal<string>('');
  siaAILoadingTextIsPulsing = signal<boolean>(false);
  private loadingTexts = [
    'Elaborazione in corso...',
    'Analizzando la richiesta...',
    'Cercando documenti rilevanti...',
    'Preparando la risposta...',
    'Processando i dati...',
    'Consultando la documentazione...',
    'Generando contenuto...',
    'Verificando le informazioni...',
    'Finalizzando la risposta...',
    'Quasi pronto...',
    'Facendo due chiacchiere con il database...',
    'Mescolando bit e byte...',
    'Convincendo gli algoritmi a collaborare...',
    'Sfogliando la biblioteca digitale...',
    'Aspettando che finisca il caffè...',
    "Traducendo dal binario all'italiano...",
  ];
  private loadingTextInterval?: any;
  private currentLoadingIndex = 0;
  private typewriterTimeout?: any;
  private currentTypingText = '';
  private currentTypingIndex = 0;
  private isTyping = false;
  private isDeleting = false;
  selectedPreviewDocument = signal<DocumentationOrigin | null>(null);
  hideChatButton = signal<boolean>(false);

  constructor() {
    // When forceEmbedded is true, lock chat in fullscreen (embedded) mode
    effect(() => {
      if (this.forceEmbedded()) {
        this.lockChatFullScreen.set(true);
      }
    });

    // Covers the initial state before ResizeObserver is set up in ngAfterViewInit:
    // if forceNarrow is already true when the component is created, isNarrow must
    // be set immediately so the first render is correct. The ResizeObserver handles
    // subsequent updates (including forceNarrow changes) once it is initialized.
    effect(() => {
      if (this.forceNarrow()) {
        this.isNarrow.set(true);
      }
    });

    // Initialize prompt position
    let sub1 = this.#siaNewRouterService.routeChanged$.subscribe(() => {
      let pageData = this.#siaNewRouterService.getLatestRoutingData();
      this.lockChatFullScreen.set(
        this.forceEmbedded() || pageData?.componentId == 'ai-chat',
      );
      this.hideChatButton.set(pageData?.componentId == 'ai-assistant');
      const dataForOutlet: { [key: string]: SiaRouteData | undefined } = {};
      const outletGuids = this.#siaNewRouterService.currentOutletsGuid;
      const keys = Object.keys(outletGuids);
      for (const key of keys) {
        const routingData =
          this.#siaNewRouterService.routingData[outletGuids[key]];
        if (routingData) {
          dataForOutlet[key] = routingData;
        }
      }
      if (keys.length > 0) {
        if (!keys.includes(this.selectedOutlet())) {
          this.selectedOutlet.set('generic');
        }
      }
      this.routeDataForOutlet.set(dataForOutlet);
    });
    this.#subs.push(sub1);

    let sub2 = this.#siaSignalrService.profileNotifications$.subscribe(
      async (msg: SiaProfileNotification) => {
        if (msg.notificationType == 'documentation-origins') {
          let origins: DocumentationOrigin[] = JSON.parse(
            msg.description ?? '[]',
          );
          let chatMessageId = msg.externalId ?? '';

          // Check if this message belongs to a cancelled request
          // Find which session this message belongs to by checking all sessions
          let targetSessionId: string | null = null;
          let messageExists = false;

          // Search through all chat histories to find which session contains this message
          const allChatHistories = this.chatHistory();
          for (const [sessionId, sessionHistory] of Object.entries(
            allChatHistories,
          )) {
            if (
              sessionHistory.some((msg) => msg.chatUniqueId === chatMessageId)
            ) {
              targetSessionId = sessionId;
              messageExists = true;
              break;
            }
          }

          if (!messageExists || !targetSessionId) {
            console.log(
              'Ignoring documentation origins for non-existent message:',
              chatMessageId,
            );
            return;
          }

          // Check if this request is still active for the target session
          const sessionRequests = this.#sessionRequestIds();
          const sessionActiveRequest = sessionRequests[targetSessionId];

          if (sessionActiveRequest && sessionActiveRequest !== chatMessageId) {
            console.log(
              'Ignoring documentation origins for cancelled request:',
              chatMessageId,
            );
            return;
          }

          let currentChatHistoryOrigins = this.chatHistoryOrigins();

          // Initialize session object if it doesn't exist
          if (!currentChatHistoryOrigins[targetSessionId]) {
            currentChatHistoryOrigins[targetSessionId] = {};
          }

          // Re-process the message to update doc references with actual file names
          const sessionHistory = allChatHistories[targetSessionId];
          const messageToUpdate = sessionHistory.find(
            (msg) => msg.chatUniqueId === chatMessageId,
          );

          currentChatHistoryOrigins[targetSessionId][chatMessageId] = origins;
          this.chatHistoryOrigins.set({});
          this.chatHistoryOrigins.set(currentChatHistoryOrigins);
          if (messageToUpdate && messageToUpdate.ai) {
            this.updateDocumentOrigins(targetSessionId, chatMessageId);
            // Process the AI response (decode Unicode only)
            const processedResponse = messageToUpdate.ai;

            // Add a marker to trigger pipe re-evaluation (will be ignored by pipe)
            // This ensures the pipe re-runs when origins become available
            const markedResponse = `${processedResponse}<!-- origins:${origins.length} -->`;

            // Update the message in chat history (pipe will handle doc refs and color tags)
            this.chatHistory.update((histories) => {
              const updatedHistory = histories[targetSessionId].map((msg) =>
                msg.chatUniqueId === chatMessageId
                  ? { ...msg, aiSanitized: markedResponse }
                  : msg,
              );
              return { ...histories, [targetSessionId]: updatedHistory };
            });

            // Sync the session with updated history
            this.syncSessionWithChatHistory(targetSessionId);
          }
        }
      },
    );
    this.#subs.push(sub2);

    let sub3 = this.#siaNewRouterService.routeModeChanged$.subscribe(
      (routeMode: SiaRouteMode) => {
        for (const key in this.routeDataForOutlet()) {
          const routeData = this.routeDataForOutlet()[key];
          if (routeData && routeData.guid == routeMode.guid) {
            let data = this.routeDataForOutlet();
            data[key] = {
              ...data[key],
              mode: routeMode.mode,
            };
            this.routeDataForOutlet.set({});
            this.routeDataForOutlet.set(data);
          }
        }
      },
    );
    this.#subs.push(sub3);
    let sub4 = this.#siaSizeDetectorService.onResize$.subscribe(
      (windowSize) => {
        const wasMobile = this.isMobile();
        const isMobileNow = windowSize?.isMobile ?? false;
        this.isMobile.set(isMobileNow);

        // Auto-enable fullscreen when switching to mobile and prompt is shown
        if (!wasMobile && isMobileNow && this.showPrompt()) {
          this.isUserFullscreen.set(true);
        }
        // Auto-disable fullscreen when switching to desktop
        if (wasMobile && !isMobileNow && this.isUserFullscreen()) {
          this.isUserFullscreen.set(false);
        }
      },
    );
    this.#subs.push(sub4);
    effect(
      () => {
        let filesToChatWith = this.#siaAIChatService.filesToChatWith();
        if (filesToChatWith.length == 0) {
          return;
        }
        this.startChatWithProvidedDocuments(filesToChatWith);
        // Reset the signal after processing to prevent re-triggering
        this.#siaAIChatService.filesToChatWith.set([]);
      },
      { allowSignalWrites: true },
    );
    effect(() => {
      this.selectedOutlet();
      let outletCollections = this.filteredCollections()[this.selectedOutlet()];
      if (
        outletCollections &&
        !outletCollections.some((x) => x.id == this.selectedCollectionId())
      ) {
        console.log('Possible collections from outlet:', outletCollections);
        // Do not set a default collection
        // this.selectedCollectionId.set(
        //   outletCollections.length > 0 ? outletCollections[0].id : '',
        // );
      }
    });

    // Reset selected categories when collection changes and select all by default
    effect(() => {
      this.selectedCollectionId();
      // When collection changes, select all available categories by default
      const allCategoryIds = new Set<string>();
      this.selectedCategoryIds.set(new Set<string>());
    });

    effect(async () => {
      this.selectedCollectionId();
      if (
        this.selectedCollectionId() == 'documentation' &&
        this.documentationCategories().length === 0
      ) {
        var categories =
          (await this.#siaAIChatService.getDocumentationCategories()) || [];
        if (categories.length > 0) {
          this.documentationCategories.set(categories);
        }
      }
    });

    // Update tree data source when tree categories change
    effect(() => {
      const categories = this.treeCategories();
      this.treeDataSource.data = categories;

      // Auto-expand all parent nodes by default
      categories.forEach((parent) => {
        if (parent.children && parent.children.length > 0) {
          this.treeControl.expand(parent);
        }
      });
    });
  }

  updateDocumentOrigins(targetSessionId: string, chatMessageId: string) {
    const messageToUpdate = this.chatHistory()[targetSessionId].find(
      (msg) => msg.chatUniqueId === chatMessageId,
    );
    const docPattern = /\[?doc_\d+(?:\s*,\s*doc_\d+)*\]?/gi;
    let allDocNumbers: number[] = [];

    if (messageToUpdate?.ai) {
      const matches = messageToUpdate.ai.match(docPattern);

      if (matches) {
        matches.forEach((match) => {
          const numbersInMatch = match
            .replace(/[\[\]]/g, '')
            .split(',')
            .map((ref: string) => ref.trim().replace(/doc_/i, ''))
            .filter((num: string) => num)
            .map((num: string) => parseInt(num, 10));
          allDocNumbers = allDocNumbers.concat(numbersInMatch);
        });
      }
    }
    const usedDocNumbersSet = new Set(allDocNumbers);
    if (!usedDocNumbersSet.size) {
      return;
    }
    // Update origins with used document information
    console.log('usedDocNumbers', usedDocNumbersSet);
    let origins = this.chatHistoryOrigins()[targetSessionId][chatMessageId];
    console.log('origins', origins);
    origins.forEach((origin) => {
      if (usedDocNumbersSet.has(origin.documentIndex ?? 0)) {
        origin.usedDocument = true;
        let iconAndColor = getDocumentIcon(origin.fileName);
        origin.icon = iconAndColor.icon;
        origin.color = iconAndColor.color;
        origin.canPreview = iconAndColor.canPreview;
      }
    });
    let currentOrigins = this.chatHistoryOrigins()[targetSessionId];
    this.chatHistoryOrigins.set({
      ...this.chatHistoryOrigins(),
      [targetSessionId]: {
        ...currentOrigins,
        [chatMessageId]: origins,
      },
    });
  }

  ngAfterViewInit() {
    // Observe host element width to toggle narrow mode
    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      this.isNarrow.set(this.forceNarrow() || width < 1000);
    });
    this.#resizeObserver.observe(this.#elementRef.nativeElement);

    // Observe DOM changes to recalculate prompt position
    let s0 = this.#siaSizeDetectorService.onResize$.subscribe(() => {
      this.calculatePromptPosition();
    });
    this.#subs.push(s0);
    this.mutationObserver = new MutationObserver(() => {
      this.calculatePromptPosition();
    });
    this.mutationObserver.observe(this.#elementRef.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  togglePrompt() {
    // Only open prompt if not currently dragging
    if (!this.isDragging()) {
      const willShowPrompt = !this.showPrompt();
      this.showPrompt.set(willShowPrompt);

      // Automatically enable fullscreen when opening prompt on mobile
      if (willShowPrompt && this.isMobile()) {
        this.isUserFullscreen.set(true);
      }

      // Calculate position immediately and after a delay to ensure correct positioning after animations
      this.calculatePromptPosition();
      setTimeout(() => {
        this.calculatePromptPosition();
      }, 350);
    } else {
      this.showPrompt.set(false);
    }
  }

  closePrompt() {
    this.showPrompt.set(false);
    // Also disable fullscreen when closing on mobile
    if (this.isMobile() && this.isUserFullscreen()) {
      this.isUserFullscreen.set(false);
    }
  }

  toggleFullscreen() {
    this.mutationObserver?.disconnect();
    this.isUserFullscreen.set(!this.isUserFullscreen());

    // Recalculate position after transition ends (300ms + buffer)
    setTimeout(() => {
      this.calculatePromptPosition();
      const container = document.getElementById('aiChatPromptContainer');
      const width = container?.offsetWidth ?? this.#elementRef.nativeElement.offsetWidth ?? 0;
      this.isNarrow.set(this.forceNarrow() || width < 1000);
      this.mutationObserver?.observe(this.#elementRef.nativeElement, {
        childList: true,
        subtree: true,
      });
    }, 350);
  }

  toggleMobileHistory() {
    this.showMobileHistory.set(!this.showMobileHistory());
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent | Event) {
    if (this.isUserFullscreen()) {
      event.preventDefault();
      this.isUserFullscreen.set(false);
    }
  }

  async sendPrompt() {
    // Create new session only if there's no active session at all
    if (!this.activeChatSessionId()) {
      this.createNewChatSession();
    }

    // Now check for ongoing requests after ensuring we have a session
    const sessionId = this.activeChatSessionId();
    const isLoading = this.chatLoading()[sessionId];
    const sessionRequests = this.#sessionRequestIds();
    const hasCurrentRequest = sessionRequests[sessionId];
    console.log('hasCurrentRequest:', hasCurrentRequest);

    // If there's an ongoing request (either chatLoading is true OR currentRequestId exists), cancel it
    if (isLoading || hasCurrentRequest) {
      console.log('🔴 Cancelling ongoing request...');

      // Actually cancel the HTTP request using the requestId
      if (hasCurrentRequest) {
        // Call the cancel endpoint on the chat-ai controller
        this.#siaHttpService.cancelRequest('chat-ai/cancel', hasCurrentRequest);
        console.log('🔴 HTTP request cancelled:', hasCurrentRequest);
      }

      // Find the last message in the current session to restore it
      const currentHistory = this.chatHistory()[sessionId] || [];
      const lastMessage = currentHistory[currentHistory.length - 1];

      // If there's a loading message, restore its input and remove it from history
      if (lastMessage && lastMessage.isLoading) {
        // Restore the user's message to the input field
        this.chatInput = lastMessage.user;

        // Restore uploaded files if they existed
        if (
          lastMessage.attachedDocuments &&
          lastMessage.attachedDocuments.length > 0
        ) {
          this.currentUploadedFiles.set(lastMessage.attachedDocuments);
        }

        // Remove the loading message from history
        const updatedHistory = currentHistory.slice(0, -1);
        this.chatHistory.set({
          ...this.chatHistory(),
          [sessionId]: updatedHistory,
        });

        // Also update the session messages
        this.syncSessionWithChatHistory(sessionId);
      }

      // Mark the current request as cancelled for this session
      this.#sessionRequestIds.set({
        ...this.#sessionRequestIds(),
        [sessionId]: null,
      });
      // Also clear the global current request if it matches this session
      if (this.#currentRequestId() === hasCurrentRequest) {
        this.#currentRequestId.set(null);
      }
      // Reset loading state
      this.chatLoading.set({
        ...this.chatLoading(),
        [sessionId]: false,
      });

      // Stop loading text rotation
      this.stopLoadingTextRotation();

      console.log(
        '🔴 Request cancelled - message restored, loading message removed',
      );
      // Don't proceed with the new request yet - let user send again
      return;
    }

    // Validate input AFTER checking for cancellation
    if (
      (!this.chatInput.trim() && this.currentUploadedFiles().length == 0) ||
      !this.selectedCollectionId()
    ) {
      return;
    }

    if (this.voiceToTextService.started) {
      this.voiceToTextService.stop();
      this.#voiceChangeSub?.unsubscribe();
    }

    // Save user input and files before clearing
    const userInput = this.chatInput;
    const userFiles = jsonDeepClone(this.currentUploadedFiles());

    // Generate unique ID for this chat
    var chatUniqueId = Guid.create().toString();

    // Set this as the current request for both global and session tracking
    this.#currentRequestId.set(chatUniqueId);
    this.#sessionRequestIds.set({
      ...this.#sessionRequestIds(),
      [sessionId]: chatUniqueId,
    });

    // Set loading state immediately to enable cancellation
    this.chatLoading.set({
      ...this.chatLoading(),
      [this.activeChatSessionId()]: true,
    });

    // Start loading text rotation
    this.startLoadingTextRotation();

    // Create message object
    const newMessage = {
      user: userInput,
      aiSanitized: '',
      ai: '',
      chatUniqueId: chatUniqueId,
      attachedDocuments: userFiles,
      isLoading: true,
    };

    // Immediately show user message and add loading indicator for AI
    this.chatHistory.set({
      ...this.chatHistory(),
      [this.activeChatSessionId()]: [
        ...(this.chatHistory()[this.activeChatSessionId()] || []),
        newMessage,
      ],
    });

    // Update session title with first message if it's a new session
    if (this.chatHistory()[this.activeChatSessionId()]?.length === 1) {
      this.updateCurrentSessionTitle(userInput);
    }

    // Sync the session with the new message
    this.syncSessionWithChatHistory();

    // Clear input and files
    this.chatInput = '';
    this.currentUploadedFiles.update((files) => []);

    let latestRoutingData = this.#siaNewRouterService.getLatestRoutingData();

    var files: { base64: string; mimeType: string }[] = [];

    // Convert uploaded files
    userFiles.map((file: UploadedChatFiles) => {
      files.push({
        base64: file.base64 ?? '',
        mimeType: file.fileType ?? 'application/octet-stream',
      });
    });
    let activeChatSessionId = this.activeChatSessionId();
    this.#siaAIChatService
      .chat(
        chatUniqueId,
        this.selectedOutlet() === 'generic' ||
          this.selectedOutlet() === 'documentation'
          ? latestRoutingData?.menu?.menuType!
          : this.routeDataForOutlet()[this.selectedOutlet()]?.menu?.menuType!,
        this.selectedOutlet() === 'generic' ||
          this.selectedOutlet() === 'documentation'
          ? latestRoutingData?.componentId!
          : this.routeDataForOutlet()[this.selectedOutlet()]?.componentId!,
        this.selectedOutlet() === 'generic' ||
          this.selectedOutlet() === 'documentation'
          ? latestRoutingData?.mode!
          : this.routeDataForOutlet()[this.selectedOutlet()]?.mode!,
        this.#siaSignalrService.connectionId,
        userInput,
        this.selectedCollection()?.isDocumentationCollection === true
          ? 'documentation'
          : this.selectedCollectionId(),
        this.#siaSizeDetectorService.currentSize.deviceType ?? 'DESKTOP',
        files,
        this.#llmChatHistory(),
        // Convert selectedCategoryIds Set to the expected format for documentation categories
        Array.from(this.selectedCategoryIds()),
        // Use reference documents array if available, otherwise use selected collection ID
        (() => {
          const referenceDocuments = this.currentSessionReferenceDocuments();
          if (referenceDocuments.length > 0) {
            // For sessions with reference documents, pass array of all document SHA values
            return referenceDocuments
              .map((doc) => doc.fileSha256)
              .filter((sha) => sha !== undefined && sha !== null) as string[];
          }
          // For non-reference document sessions, return null (no specific documents)
          return null;
        })(),
        chatUniqueId, // Pass the requestId for HTTP cancellation
      )
      .then(async (res: string) => {
        // Check if this request is still valid for this specific session
        const currentSessionRequests = this.#sessionRequestIds();
        if (currentSessionRequests[activeChatSessionId] === chatUniqueId) {
          console.time('Set AI Response Time');
          await this.setAIresponse(res, chatUniqueId, activeChatSessionId);
          console.timeEnd('Set AI Response Time');
        } else {
          console.log(
            'Request was cancelled, ignoring response for:',
            chatUniqueId,
          );
        }
      })
      .catch(async (error) => {
        // Only show error if this request is still current for this session

        console.log('AI Chat Error:', error);
        const currentSessionRequests = this.#sessionRequestIds();
        if (currentSessionRequests[activeChatSessionId] === chatUniqueId) {
          // Check if this is an abort error (request was cancelled)
          if (error.name === 'AbortError') {
            console.log('Request was aborted by user:', chatUniqueId);
            // Don't show error message for user-initiated cancellations
            return;
          }

          await this.setAIresponse(
            '### 🚨 Impossibile rispondere al momento.',
            chatUniqueId,
            activeChatSessionId,
          );
        } else {
          console.log(
            'Request was cancelled, ignoring error for:',
            chatUniqueId,
          );
        }
      })
      .finally(async () => {
        // Only update loading state if this request is still current for this session
        const currentSessionRequests = this.#sessionRequestIds();
        if (currentSessionRequests[activeChatSessionId] === chatUniqueId) {
          this.chatLoading.set({
            ...this.chatLoading(),
            [activeChatSessionId]: false,
          });

          // Stop loading text rotation
          this.stopLoadingTextRotation();

          // Clear current request for this session
          this.#sessionRequestIds.set({
            ...this.#sessionRequestIds(),
            [activeChatSessionId]: null,
          });

          // Also clear global current request if it matches
          if (this.#currentRequestId() === chatUniqueId) {
            this.#currentRequestId.set(null);
          }

          setTimeout(() => {
            this.chatInputElement?.nativeElement.focus();
          });
        }
      });

    // Optionally close prompt after sending
    // this.closePrompt();
  }

  private async setAIresponse(
    res: string,
    chatUniqueId: string,
    activeChatSessionId: string,
  ) {
    // Double-check that this request hasn't been cancelled for this specific session
    const currentSessionRequests = this.#sessionRequestIds();
    if (currentSessionRequests[activeChatSessionId] !== chatUniqueId) {
      console.log(
        'Skipping setAIresponse for cancelled request:',
        chatUniqueId,
      );
      return;
    }

    // Store the processed text - the pipe will handle markdown, doc references, and color tags
    // Update the existing message with AI response
    const currentHistory = this.chatHistory()[activeChatSessionId] || [];
    const messageIndex = currentHistory.findIndex(
      (msg) => msg.chatUniqueId === chatUniqueId,
    );

    if (messageIndex !== -1) {
      const updatedHistory = [...currentHistory];
      const updatedMessage = {
        ...updatedHistory[messageIndex],
        aiSanitized: res, // Store processed markdown (pipe will handle the rest)
        ai: res,
        isLoading: false,
      };

      updatedHistory[messageIndex] = updatedMessage;

      this.chatHistory.set({
        ...this.chatHistory(),
        [activeChatSessionId]: updatedHistory,
      });
      this.updateDocumentOrigins(activeChatSessionId, chatUniqueId);

      // Sync the session with the updated chat history
      this.syncSessionWithChatHistory(activeChatSessionId);
    }
  }

  // Cache for markdown contexts to prevent infinite pipe re-evaluation
  #markdownContextCache = new Map<string, any>();

  /**
   * Gets document origins for a specific message
   * @param chatUniqueId The unique ID of the chat message
   * @returns Array of document origins for this message
   */
  getMessageDocumentOrigins(chatUniqueId: string): DocumentationOrigin[] {
    const sessionId = this.activeChatSessionId();
    if (!sessionId) return [];

    const sessionOrigins = this.chatHistoryOrigins()[sessionId];
    if (!sessionOrigins) return [];

    return sessionOrigins[chatUniqueId] || [];
  }

  /**
   * Gets memoized markdown context to prevent infinite pipe re-evaluation
   * Returns the same object reference if inputs haven't changed
   * @param chatUniqueId The unique ID of the chat message
   * @returns Memoized context object for the markdown pipe
   */
  getMarkdownContext(chatUniqueId: string): any {
    const sessionId = this.activeChatSessionId();
    const documentOrigins = this.getMessageDocumentOrigins(chatUniqueId);

    // Create a cache key based on relevant values
    const cacheKey = `${chatUniqueId}:${sessionId}:${documentOrigins.length}:${documentOrigins.map((d) => d.fileSha256).join(',')}`;

    // Check if we have a cached context with the same key
    const cachedEntry = this.#markdownContextCache.get(chatUniqueId);
    if (cachedEntry && cachedEntry.key === cacheKey) {
      return cachedEntry.context;
    }

    // Create new context object
    const context = {
      chatUniqueId,
      sessionId,
      documentOrigins,
    };

    // Cache it
    this.#markdownContextCache.set(chatUniqueId, { key: cacheKey, context });

    return context;
  }

  /**
   * Handles clicks on document reference links
   * @param chatUniqueId The unique ID of the chat message
   * @param fileShas Comma-separated string of fileSha256 values
   */
  onDocReferenceLinkClick(chatUniqueId: string, fileShas: string): void {
    const shaValues = fileShas.split(',').map((sha) => sha.trim());
    console.log('Document reference clicked:', { chatUniqueId, shaValues });

    // Find the session that contains this message
    let targetSessionId: string | null = null;
    const allChatHistories = this.chatHistory();
    for (const [sessionId, sessionHistory] of Object.entries(
      allChatHistories,
    )) {
      if (sessionHistory.some((msg) => msg.chatUniqueId === chatUniqueId)) {
        targetSessionId = sessionId;
        break;
      }
    }

    if (!targetSessionId) {
      notify(
        'Impossibile trovare la sessione di chat per questo messaggio',
        3000,
        'warning',
      );
      return;
    }

    // Get the chat origins for this specific message
    const sessionOrigins = this.chatHistoryOrigins()[targetSessionId];
    const origins = sessionOrigins?.[chatUniqueId];

    if (origins && origins.length > 0) {
      // Find and open documents matching the fileSha256 values
      shaValues.forEach((sha) => {
        const origin = origins.find((o) => o.fileSha256 === sha);
        if (origin) {
          this.previewDocument(origin);
        }
      });
    } else {
      notify(
        'Nessun documento di origine disponibile per questo riferimento',
        3000,
        'warning',
      );
    }
  }

  /**
   * Shows a mat-menu with document options when hovering over a multi-document reference
   * @param event The mouse event
   * @param chatUniqueId The unique ID of the chat message
   * @param fileShas Comma-separated string of fileSha256 values
   */
  showDocumentSelectionMenu(
    event: MouseEvent,
    chatUniqueId: string,
    fileShas: string,
  ): void {
    const shaValues = fileShas.split(',').map((sha) => sha.trim());

    // Find the session that contains this message
    let targetSessionId: string | null = null;
    const allChatHistories = this.chatHistory();
    for (const [sessionId, sessionHistory] of Object.entries(
      allChatHistories,
    )) {
      if (sessionHistory.some((msg) => msg.chatUniqueId === chatUniqueId)) {
        targetSessionId = sessionId;
        break;
      }
    }

    if (!targetSessionId) {
      notify(
        'Impossibile trovare la sessione di chat per questo messaggio',
        3000,
        'warning',
      );
      return;
    }

    // Get the chat origins for this specific message
    const sessionOrigins = this.chatHistoryOrigins()[targetSessionId];
    const origins = sessionOrigins?.[chatUniqueId];

    if (!origins || origins.length === 0) {
      notify(
        'Nessun documento di origine disponibile per questo riferimento',
        3000,
        'warning',
      );
      return;
    }

    // Filter origins to only the referenced documents
    const referencedDocs = shaValues
      .map((sha) => origins.find((o) => o.fileSha256 === sha))
      .filter((doc): doc is DocumentationOrigin => doc !== undefined);

    if (referencedDocs.length === 0) {
      notify('Nessun documento valido trovato', 3000, 'warning');
      return;
    }

    // Store documents for the menu
    this.docMenuDocuments.set(referencedDocs);

    // Set menu position
    this.docMenuPosition.set({
      x: `${event.clientX}px`,
      y: `${event.clientY}px`,
    });

    // Open menu programmatically using the trigger
    if (this.docReferenceMenuTrigger) {
      // Trigger the menu to open
      setTimeout(() => {
        this.docReferenceMenuTrigger.openMenu();
      }, 0);
    }
  }

  /**
   * Opens a document from the reference menu
   * @param doc The document to open
   */
  openDocumentFromMenu(doc: DocumentationOrigin): void {
    this.previewDocument(doc);
    // Close the menu
    if (this.docReferenceMenuTrigger) {
      this.docReferenceMenuTrigger.closeMenu();
    }
  }

  /**
   * Handles clicks on message content to intercept doc reference links
   * @param event The click event
   * @param chatUniqueId The unique ID of the chat message
   */
  onMessageContentClick(event: MouseEvent, chatUniqueId: string): void {
    const target = event.target as HTMLElement;
    console.log('Message content clicked:', target);

    // Check if the clicked element is a doc reference link or a child of one
    const link = target.closest('a.doc-reference-link') as HTMLAnchorElement;

    if (link) {
      event.preventDefault();
      event.stopPropagation();

      // Check if this is a multiple document reference
      const isMultiple = link.classList.contains('doc-reference-multiple');

      // Parse the href which has format: #doc-ref-{chatId}-{fileSha256Values}
      const href = link.getAttribute('href');
      if (href && href.startsWith('#doc-ref-')) {
        const hrefContent = href.substring(9); // Remove '#doc-ref-' prefix

        // The format is: {chatId}-{fileSha256,fileSha256,...}
        // ChatId is a GUID with format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (5 parts separated by -)
        const parts = hrefContent.split('-');

        if (parts.length >= 6) {
          // At least 5 parts for GUID + 1 for SHA
          // First 5 parts form the chatId GUID
          const linkChatId = parts.slice(0, 5).join('-');
          // Everything after is the comma-separated fileSha256 values (may contain hyphens)
          const fileShas = parts.slice(5).join('-');

          if (fileShas && linkChatId) {
            if (isMultiple) {
              // Show menu for multiple documents
              this.showDocumentSelectionMenu(event, linkChatId, fileShas);
            } else {
              // Direct open for single document
              this.onDocReferenceLinkClick(linkChatId, fileShas);
            }
          }
        }
      }
    }
  }

  clearInput() {
    this.chatInput = '';
  }

  onDragStarted() {
    this.isDragging.set(true);
    this.showPrompt.set(false);
  }

  onDragEnded() {
    // Add a small delay to prevent immediate opening after drag
    setTimeout(() => {
      this.isDragging.set(false);
    }, 100);
  }

  onTextareaKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift+Enter: Allow default behavior (new line)
        return;
      } else {
        // Enter only: Send prompt if conditions are met
        event.preventDefault();
        if (
          this.chatInput.trim() &&
          !this.chatLoading()[this.activeChatSessionId()] &&
          this.selectedCollectionId()
        ) {
          this.sendPrompt();
        }
      }
    }
  }

  private calculatePromptPosition(
    overrideWidth?: number,
    overrideHeight?: number,
  ) {
    // Don't calculate position for fullscreen mode
    if (this.isUserFullscreen()) {
      return;
    }

    let fabButton = $('#aiChatFabButton');
    if (fabButton && fabButton.length) {
      const fabRect = fabButton[0].getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const promptWidth =
        overrideWidth ?? $('#aiChatPromptContainer').outerWidth(); // Approximate width of the prompt dialog
      const promptHeight =
        overrideHeight ?? $('#aiChatPromptContainer').outerHeight(); // Approximate height of the prompt dialog
      if (!promptWidth || !promptHeight) {
        return;
      }

      // Detect which corner the FAB is closest to
      const isLeft = fabRect.left < windowWidth / 2;
      const isTop = fabRect.top < windowHeight / 2;

      let x = isLeft ? fabRect.left : fabRect.right - promptWidth;
      let y = isTop ? fabRect.bottom + 10 : fabRect.top - promptHeight - 10;

      // Ensure prompt doesn't go off screen
      x = Math.max(10, Math.min(x, windowWidth - promptWidth - 10));
      y = Math.max(10, Math.min(y, windowHeight - promptHeight - 10));
      if (this.isUserFullscreen()) {
        return;
      }

      this.promptPosition = { x, y };
    }
  }

  toggleVoiceInput() {
    if (this.voiceToTextService.started) {
      this.voiceToTextService.stop();
      this.#voiceChangeSub?.unsubscribe();
      return;
    }
    this.#voiceChangeSub?.unsubscribe();
    this.#voiceChangeSub = this.voiceToTextService
      .start()
      .subscribe((text: string) => {
        this.chatInput = text;
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);

      if (
        this.currentUploadedFiles().length + files.length >
        this.maxFileNumber
      ) {
        notify(
          `E' possibile caricare al massimo ${this.maxFileNumber} file`,
          3000,
          'error',
        );
        return;
      }

      files.forEach((file) => {
        const reader = new FileReader();

        // Get just the extension (e.g., "pdf" from "application/pdf")
        var simplifiedType = file.type.split('/')[1] || 'unknown';

        reader.onload = () => {
          var base64String = (reader.result as string).split(',')[1];
          var newFile: UploadedChatFiles = {
            fileName: file.name,
            fileType: file.type,
            simplifiedFileType: simplifiedType,
            base64: base64String,
          };

          this.currentUploadedFiles.update((files) => [...files, newFile]);
        };

        reader.onerror = (error) => {
          console.error(`Error reading file ${file.name}:`, error);
        };

        reader.readAsDataURL(file); // Converts to base64
      });
      input.value = ''; // reset cosi è possibile reinserire il file
    }
  }

  removeFile(fileName: string) {
    this.currentUploadedFiles.update((files) =>
      files.filter((file) => file.fileName !== fileName),
    );
  }

  useMCPCollection(toolId: string, outlet: string) {
    // Prevent collection changes if the current session is locked
    if (this.isCollectionLocked()) {
      return;
    }

    console.log('Selezionata collezione', toolId, 'per outlet', outlet);
    this.selectedCollectionId.set(toolId);
    this.selectedOutlet.set(outlet);
    this.chatSessions.update((sessions) => {
      let currentChatSessionIndex = sessions.findIndex(
        (s) => s.id === this.activeChatSessionId(),
      );
      if (currentChatSessionIndex == -1) {
        return sessions;
      }
      sessions[currentChatSessionIndex].collectionId = toolId;
      sessions[currentChatSessionIndex].outlet = outlet;
      return [...sessions];
    });
  }

  scrollChatContainerToBottom() {
    clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = setTimeout(() => {
      const container = $('#chatContainer')[0];
      const assistantMessages = container.querySelectorAll(
        '.ai-chat-assistant-message',
      );
      const lastAssistantMessage = assistantMessages[
        assistantMessages.length - 1
      ] as HTMLElement;
      if (lastAssistantMessage) {
        const scrollTarget =
          lastAssistantMessage.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      } else {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }, 200);
  }

  newChat() {
    // Find if an empty session already exists
    console.log(
      'Sessioni di chat pre update',
      jsonDeepClone(this.chatSessions()),
    );
    let emptySessionKey = '';
    for (let sessionKey in this.chatHistory()) {
      if (this.chatHistory()[sessionKey].length == 0) {
        this.activeChatSessionId.set(sessionKey);
        emptySessionKey = sessionKey;
        break; // Found empty session, no need to continue
      }
    }

    // Create a new session if no empty session exists AND current session has messages
    const activeSessionId = this.activeChatSessionId();
    const currentHistory = this.chatHistory()[activeSessionId] || [];

    if (!emptySessionKey && currentHistory.length > 0) {
      let guid = this.createNewChatSession();
      this.activeChatSessionId.set(guid);
    }

    // Clear markdown context cache when starting new chat
    this.#markdownContextCache.clear();

    // Always reset the collection and outlet when opening a new chat
    const allCollections = Object.values(this.filteredCollections()).flat();
    if (allCollections.length != 1) {
      this.selectedCollectionId.set('');
      this.selectedOutlet.set('');
    }

    console.log('Sessioni di chat', jsonDeepClone(this.chatSessions()));
    this.chatSessions.update((sessions) => {
      let currentChatSessionIndex = sessions.findIndex(
        (s) => s.id === this.activeChatSessionId(),
      );
      if (currentChatSessionIndex == -1) {
        return sessions;
      }
      sessions[currentChatSessionIndex].collectionId = '';
      sessions[currentChatSessionIndex].outlet = '';
      return [...sessions];
    });

    // Clear selected documents when starting a new chat
    this.selectedDocuments.set(new Set());

    // Clear reference documents for the current session if switching to collection selection
    const newCurrentSessionId = this.activeChatSessionId();
    if (newCurrentSessionId && !this.selectedCollectionId()) {
      this.sessionReferenceDocuments.set({
        ...this.sessionReferenceDocuments(),
        [newCurrentSessionId]: [],
      });
    }

    if (this.isMobile()) {
      this.showMobileHistory.set(false);
    }
    if (allCollections.length === 1) {
      for (let key in this.filteredCollections()) {
        if (this.filteredCollections()[key].length == 1) {
          this.useMCPCollection(this.filteredCollections()[key][0].id, key);
        }
      }
    }
  }

  createNewChatSession() {
    const sessionId = Guid.create().toString();
    const title = `Chat ${this.chatSessions().length + 1}`;

    const newSession = {
      id: sessionId,
      title: title,
      collectionId: this.selectedCollectionId(),
      outlet: this.selectedOutlet(),
      lastActivity: new Date(),
      messages: [],
    };

    this.chatSessions.update((sessions) => [newSession, ...sessions]);
    this.activeChatSessionId.set(sessionId);

    // Clear current chat history to start fresh
    this.chatHistory.update((history) => {
      history[this.activeChatSessionId()] = [];
      return { ...history };
    });

    // Initialize session request tracking
    this.#sessionRequestIds.set({
      ...this.#sessionRequestIds(),
      [sessionId]: null,
    });

    // Update global current request (clear it since this is a new session)
    this.#currentRequestId.set(null);

    // Clear selected documents when creating new session
    this.selectedDocuments.set(new Set());

    // Initialize empty reference documents for the new session
    this.sessionReferenceDocuments.set({
      ...this.sessionReferenceDocuments(),
      [sessionId]: [],
    });

    return sessionId;
  }

  loadChatSession(sessionId: string) {
    const session = this.chatSessions().find((s) => s.id === sessionId);
    if (session) {
      console.log('Sessione attuale', session);
      this.activeChatSessionId.set(sessionId);
      this.selectedCollectionId.set(session.collectionId);
      this.selectedOutlet.set(session.outlet);

      // Clear markdown context cache when switching sessions
      this.#markdownContextCache.clear();

      // Load session messages into current chat history
      this.chatHistory.update((history) => {
        history[session.id] = session.messages;
        return { ...history };
      });

      // Update global current request to match this session's active request
      const sessionRequests = this.#sessionRequestIds();
      const sessionActiveRequest = sessionRequests[sessionId];
      if (sessionActiveRequest) {
        this.#currentRequestId.set(sessionActiveRequest);
      } else {
        // Only clear if no other sessions have active requests
        const hasAnyActiveRequest = Object.values(sessionRequests).some(
          (req) => req !== null,
        );
        if (!hasAnyActiveRequest) {
          this.#currentRequestId.set(null);
        }
      }

      // Clear selected documents when switching sessions
      this.selectedDocuments.set(new Set());

      if (this.isMobile()) {
        this.showMobileHistory.set(false);
      }
    }
  }

  updateCurrentSessionTitle(firstMessage: string) {
    const activeSessionId = this.activeChatSessionId();
    if (activeSessionId) {
      this.chatSessions.update((sessions) =>
        sessions.map((session) =>
          session.id === activeSessionId
            ? {
                ...session,
                title:
                  firstMessage.substring(0, 40) +
                  (firstMessage.length > 40 ? '...' : ''),
                lastActivity: new Date(),
              }
            : session,
        ),
      );
    }
  }

  saveMessageToSession(message: any) {
    const activeSessionId = this.activeChatSessionId();
    if (activeSessionId) {
      this.chatSessions.update((sessions) =>
        sessions.map((session) =>
          session.id === activeSessionId
            ? {
                ...session,
                messages: [...session.messages, message],
                lastActivity: new Date(),
              }
            : session,
        ),
      );
    }
  }

  syncSessionWithChatHistory(activeSessionId?: string) {
    activeSessionId = activeSessionId ?? this.activeChatSessionId();
    if (activeSessionId) {
      const currentHistory = this.chatHistory()[activeSessionId] || [];
      this.chatSessions.update((sessions) =>
        sessions.map((session) =>
          session.id === activeSessionId
            ? { ...session, messages: currentHistory, lastActivity: new Date() }
            : session,
        ),
      );
    }
    this.scrollChatContainerToBottom();
  }

  toggleSidebar() {
    this.sidebarClosed.update((visible) => !visible);
  }

  selectDocumentationCategory(documentationCategoryId: string) {
    let currentSelected: any = this.selectedDocumentationCategories();
    this.selectedDocumentationCategories.set({});
    if (
      Object.keys(currentSelected).findIndex(
        (key) => key === documentationCategoryId,
      ) != -1
    ) {
      delete currentSelected[documentationCategoryId];
      this.selectedDocumentationCategories.set(currentSelected);
      return;
    }
    currentSelected[documentationCategoryId] = [];
    this.selectedDocumentationCategories.set(currentSelected);
  }

  // Checkbox tree methods
  isChecked(categoryId: string): boolean {
    return this.selectedCategoryIds().has(categoryId);
  }

  isIndeterminate(parentCategory: any): boolean {
    const children = parentCategory.children || [];
    const checkedChildren = children.filter((child: any) =>
      this.selectedCategoryIds().has(child.id),
    );
    return (
      checkedChildren.length > 0 && checkedChildren.length < children.length
    );
  }

  toggleCategory(categoryId: string): void {
    const selected = new Set(this.selectedCategoryIds());

    if (selected.has(categoryId)) {
      selected.delete(categoryId);
    } else {
      selected.add(categoryId);
    }

    this.selectedCategoryIds.set(selected);
    this.updateParentChildSelection(categoryId);
  }

  toggleParentCategory(parentCategory: any, $event: MatCheckboxChange): void {
    const selected = new Set(this.selectedCategoryIds());
    const children = parentCategory.children || [];

    // Check if all children are selected
    const allChildrenSelected = children.every((child: any) =>
      selected.has(child.id),
    );
    console.log('allChildrenSelected', allChildrenSelected);
    console.log('children', children);
    console.log('Selezionate', selected);
    if (allChildrenSelected || !$event.checked) {
      // Uncheck parent and all children
      selected.delete(parentCategory.id);
      children.forEach((child: any) => selected.delete(child.id));
    } else {
      // Check parent and all children
      selected.add(parentCategory.id);
      children.forEach((child: any) => selected.add(child.id));
    }

    this.selectedCategoryIds.set(selected);
  }

  private updateParentChildSelection(categoryId: string): void {
    const selected = new Set(this.selectedCategoryIds());

    // Find if this is a child category and update parent accordingly
    this.treeCategories().forEach((parent) => {
      const children = parent.children || [];
      const isChild = children.some((child: any) => child.id === categoryId);

      if (isChild) {
        const selectedChildren = children.filter((child: any) =>
          selected.has(child.id),
        );

        if (selectedChildren.length === 0) {
          // No children selected, uncheck parent
          selected.delete(parent.id);
        } else if (selectedChildren.length === children.length) {
          // All children selected, check parent
          selected.add(parent.id);
        }
      }
    });

    this.selectedCategoryIds.set(selected);
  }

  // Tree node expansion methods
  hasChild = (_: number, node: AIDocumentationCategories) =>
    !!node.children && node.children.length > 0;

  // Document selection methods
  toggleDocumentSelection(documentId: string): void {
    const selected = new Set(this.selectedDocuments());
    if (selected.has(documentId)) {
      selected.delete(documentId);
    } else {
      selected.add(documentId);
    }
    this.selectedDocuments.set(selected);
  }

  isDocumentSelected(documentId: string): boolean {
    return this.selectedDocuments().has(documentId);
  }

  async previewDocument(document: DocumentationOrigin) {
    console.log('previewDocument', document);
    this.selectedPreviewDocument.set(document);
  }

  chatWithSelectedDocuments(): void {
    // If no documents are selected, return early
    if (this.selectedDocuments().size === 0) {
      return;
    }

    // Get the selected document IDs and convert to documents
    const selectedDocumentIds = Array.from(this.selectedDocuments());
    const currentDocuments = this.currentSessionDocuments();
    const selectedDocuments = currentDocuments.filter((doc) =>
      selectedDocumentIds.includes(doc.fileSha256 || ''),
    );

    if (selectedDocuments.length === 0) {
      console.warn('No valid documents found for selected IDs');
      return;
    }

    // Create a new chat session
    this.startChatWithProvidedDocuments(selectedDocuments);
  }

  private startChatWithProvidedDocuments(
    selectedDocuments: DocumentationOrigin[],
  ) {
    const newSessionId = this.createNewChatSession();

    // Set collection to documentation and outlet to documentation
    this.selectedCollectionId.set('documentation');
    this.selectedOutlet.set('documentation');

    // Update the new session to reflect the collection and outlet
    this.chatSessions.update((sessions) => {
      const sessionIndex = sessions.findIndex((s) => s.id === newSessionId);
      if (sessionIndex !== -1) {
        sessions[sessionIndex].collectionId = 'documentation';
        sessions[sessionIndex].outlet = 'documentation';
        sessions[sessionIndex].title =
          `Chat con ${selectedDocuments.length} document${selectedDocuments.length > 1 ? 'i' : 'o'}`;
      }
      return [...sessions];
    });

    // Store the selected documents as reference documents for this session
    this.sessionReferenceDocuments.set({
      ...this.sessionReferenceDocuments(),
      [newSessionId]: selectedDocuments,
    });

    // Initialize chat history as empty (no messages, just the document context)
    this.chatHistory.set({
      ...this.chatHistory(),
      [newSessionId]: [],
    });

    // Sync the session (empty chat history but with document context)
    this.syncSessionWithChatHistory(newSessionId);

    // Clear selected documents after starting chat
    this.selectedDocuments.set(new Set());

    // Open the chat prompt
    if (!this.showPrompt()) {
      this.showPrompt.set(true);

      // Automatically enable fullscreen when opening prompt on mobile
      if (this.isMobile()) {
        this.isUserFullscreen.set(true);
      }

      // Calculate position after opening
      setTimeout(() => {
        this.calculatePromptPosition();
      }, 350);
    }

    // Focus on the chat input for the new session
    setTimeout(() => {
      this.chatInputElement?.nativeElement.focus();
    }, 100);
  }

  getDocumentIcon(fileName?: string): {
    icon: string;
    color: string;
    canPreview: boolean;
  } {
    return getDocumentIcon(fileName);
  }

  private startLoadingTextRotation(): void {
    this.stopLoadingTextRotation();

    this.currentLoadingIndex = 0;
    this.siaAILoadingText.set('');
    this.siaAILoadingTextIsPulsing.set(false);

    // Start the typewriter cycle
    this.startTypewriterCycle();
  }

  private startTypewriterCycle(): void {
    const currentText = this.loadingTexts[this.currentLoadingIndex];
    this.currentTypingText = currentText;
    this.currentTypingIndex = 0;
    this.isTyping = true;
    this.isDeleting = false;
    this.siaAILoadingTextIsPulsing.set(false);

    this.typeNextCharacter();
  }

  private typeNextCharacter(): void {
    if (!this.isTyping && !this.isDeleting) return;

    if (this.isTyping) {
      // Typing phase
      const currentText = this.currentTypingText.substring(
        0,
        this.currentTypingIndex + 1,
      );
      this.siaAILoadingText.set(currentText);
      this.currentTypingIndex++;

      if (this.currentTypingIndex >= this.currentTypingText.length) {
        // Finished typing, start pulse and then deletion after delay
        this.isTyping = false;
        this.siaAILoadingTextIsPulsing.set(true);

        this.typewriterTimeout = setTimeout(() => {
          this.siaAILoadingTextIsPulsing.set(false);
          this.isDeleting = true;
          this.currentTypingIndex = this.currentTypingText.length;
          this.typeNextCharacter();
        }, 3500); // 3.5 seconds pause with pulse
        return;
      }

      // Continue typing with calculated speed
      const typingSpeed = this.calculateTypingSpeed(this.currentTypingText);
      this.typewriterTimeout = setTimeout(
        () => this.typeNextCharacter(),
        typingSpeed,
      );
    } else if (this.isDeleting) {
      // Deleting phase
      this.currentTypingIndex--;
      const currentText = this.currentTypingText.substring(
        0,
        this.currentTypingIndex,
      );
      this.siaAILoadingText.set(currentText);

      if (this.currentTypingIndex <= 0) {
        // Finished deleting, move to next phrase
        this.isDeleting = false;
        this.currentLoadingIndex =
          (this.currentLoadingIndex + 1) % this.loadingTexts.length;

        this.typewriterTimeout = setTimeout(() => {
          this.startTypewriterCycle();
        }, 500); // Short pause before starting next phrase
        return;
      }

      // Continue deleting at faster speed
      this.typewriterTimeout = setTimeout(() => this.typeNextCharacter(), 50);
    }
  }

  private calculateTypingSpeed(text: string): number {
    // Target 2 seconds for long phrases, adjust for shorter ones
    const targetDuration = 2000; // 2 seconds
    const minSpeed = 40; // Minimum ms per character
    const maxSpeed = 150; // Maximum ms per character

    const calculatedSpeed = targetDuration / text.length;
    return Math.max(minSpeed, Math.min(maxSpeed, calculatedSpeed));
  }

  private stopLoadingTextRotation(): void {
    if (this.loadingTextInterval) {
      clearInterval(this.loadingTextInterval);
      this.loadingTextInterval = undefined;
    }

    if (this.typewriterTimeout) {
      clearTimeout(this.typewriterTimeout);
      this.typewriterTimeout = undefined;
    }

    // Reset all animation state
    this.isTyping = false;
    this.isDeleting = false;
    this.currentTypingIndex = 0;
    this.currentLoadingIndex = 0;
    this.siaAILoadingText.set('');
    this.siaAILoadingTextIsPulsing.set(false);
  }

  ngOnDestroy() {
    // Cancel all active HTTP requests before destroying component
    const sessionRequests = this.#sessionRequestIds();
    Object.values(sessionRequests).forEach((requestId) => {
      if (requestId) {
        this.#siaHttpService.cancelRequest(requestId);
      }
    });

    this.#voiceChangeSub?.unsubscribe();

    this.mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();

    clearTimeout(this.#scrollTimeout);

    // Clean up loading text intervals and timeouts
    clearInterval(this.loadingTextInterval);
    clearTimeout(this.typewriterTimeout);
  }

  async ngOnInit() {
    var collections = await this.#siaAIChatService.retriveCollections();
    this.collections.set(collections);
    for (let key in this.filteredCollections()) {
      if (this.filteredCollections()[key].length == 1) {
        this.useMCPCollection(this.filteredCollections()[key][0].id, key);
      }
    }
  }
}
